import { useRef, useState } from 'react';
import { inrShort, dfmt, dfmtLong } from '../../utils/format';
import { tipApi } from '../common/HoverTip';

export function niceMax(v) {
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / p;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nf * p;
}

/**
 * Multi-series daily line with a crosshair and a combined hover readout.
 * `series`: [{ name, color, data: [{ d, v }] }]
 */
export default function LineChart({ series, height = 260 }) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null); // index of the hovered day

  if (!series.length || !series[0].data.length) return null;

  const W = 760, H = height, padL = 54, padR = 16, padT = 14, padB = 34;
  const dates = series[0].data.map((p) => p.d);
  const n = dates.length;
  const maxV = Math.max(1, ...series.flatMap((s) => s.data.map((p) => p.v)));
  const nice = niceMax(maxV);
  const x = (i) => padL + (n <= 1 ? 0 : ((W - padL - padR) * i) / (n - 1));
  const y = (v) => padT + (H - padT - padB) * (1 - v / nice);
  const step = Math.ceil(n / 7);

  const locate = (ev) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = ev.clientX;
    pt.y = ev.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    const t = (p.x - padL) / (W - padL - padR);
    return Math.round(Math.min(1, Math.max(0, t)) * (n - 1));
  };

  const onMove = (ev) => {
    const i = locate(ev);
    if (i == null) return;
    setHover(i);
    const rows = series
      .map(
        (se) =>
          `<div style="display:flex;gap:10px;justify-content:space-between"><span><i style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${se.color};margin-right:5px"></i>${se.name}</span><b>${inrShort(se.data[i].v)}</b></div>`
      )
      .join('');
    tipApi.show(`<div style="margin-bottom:4px;font-weight:600">${dfmtLong(dates[i])}</div>${rows}`, ev);
  };

  const onLeave = () => {
    setHover(null);
    tipApi.hide();
  };

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ touchAction: 'none' }}>
      {[0, 1, 2, 3, 4].map((t) => {
        const gv = (nice * t) / 4;
        const gy = y(gv);
        return (
          <g key={t}>
            <line className="gl" x1={padL} y1={gy} x2={W - padR} y2={gy} />
            <text className="ax" x={padL - 8} y={gy + 3} textAnchor="end">{inrShort(gv)}</text>
          </g>
        );
      })}

      {dates.map((d, i) =>
        i % step === 0 || i === n - 1 ? (
          <text key={d} className="ax" x={x(i)} y={H - 12} textAnchor="middle">{dfmt(d)}</text>
        ) : null
      )}

      {series.map((se) => {
        const dp = se.data.map((p, i) => `${i ? 'L' : 'M'}${x(i)} ${y(p.v)}`).join(' ');
        return (
          <g key={se.name}>
            {series.length === 1 && (
              <path d={`${dp} L${x(n - 1)} ${y(0)} L${x(0)} ${y(0)} Z`} fill={se.color} opacity=".1" />
            )}
            <path d={dp} fill="none" stroke={se.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            {hover != null && (
              <circle cx={x(hover)} cy={y(se.data[hover].v)} r={4} fill={se.color} />
            )}
          </g>
        );
      })}

      {hover != null && (
        <line x1={x(hover)} y1={padT} x2={x(hover)} y2={H - padB} stroke="var(--axis)" />
      )}

      <rect
        x={padL} y={padT} width={W - padL - padR} height={H - padT - padB} fill="transparent"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onTouchMove={(e) => e.touches[0] && onMove(e.touches[0])}
      />
    </svg>
  );
}
